import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, Newspaper, Trophy, Inbox, ArrowLeft, MessageSquare, Music2, CalendarClock, MailCheck, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { API, formatApiErrorDetail } from "@/lib/api";
import { LogoMark } from "@/components/Logo";
import FansModeration from "@/components/FansModeration";
import TikTokManager from "@/components/TikTokManager";
import SubscribersList from "@/components/SubscribersList";
import MatchEditor from "@/components/MatchEditor";
import ImageUpload from "@/components/ImageUpload";
import SquadManager from "@/components/SquadManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

const EMPTY = {
  title: "", category: "articulo", tags: "", excerpt: "", cover_image: "",
  content: "", featured: false, competition: "", match_date: "",
  home_team: "", away_team: "", home_score: "", away_score: "",
  scorers: "", player_ratings: "", tactical_keys: "", lineup: "", bench: "", substitutions: "", formation: "",
};

const toForm = (a) => ({
  ...a,
  tags: (a.tags || []).join(", "),
  scorers: (a.scorers || []).join("\n"),
  player_ratings: (a.player_ratings || []).map((r) => `${r.player}:${r.rating}`).join("\n"),
  tactical_keys: (a.tactical_keys || []).map((k) => `${k.title}|${k.text}`).join("\n"),
  lineup: (a.lineup || []).join("\n"),
  bench: (a.bench || []).join("\n"),
  substitutions: (a.substitutions || []).join("\n"),
  formation: a.formation || "",
  home_score: a.home_score ?? "",
  away_score: a.away_score ?? "",
});

const toPayload = (f) => ({
  title: f.title,
  category: f.category,
  excerpt: f.excerpt,
  content: f.content,
  cover_image: f.cover_image,
  featured: !!f.featured,
  tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
  competition: f.competition || null,
  match_date: f.match_date || null,
  home_team: f.home_team || null,
  away_team: f.away_team || null,
  home_score: f.home_score === "" ? null : Number(f.home_score),
  away_score: f.away_score === "" ? null : Number(f.away_score),
  scorers: f.scorers.split("\n").map((s) => s.trim()).filter(Boolean),
  player_ratings: f.player_ratings.split("\n").map((l) => {
    const [player, rating] = l.split(":");
    return { player: (player || "").trim(), rating: parseFloat(rating) || 0 };
  }).filter((r) => r.player),
  tactical_keys: f.tactical_keys.split("\n").map((l) => {
    const [title, ...rest] = l.split("|");
    return { title: (title || "").trim(), text: rest.join("|").trim() };
  }).filter((k) => k.title),
  lineup: f.lineup.split("\n").map((s) => s.trim()).filter(Boolean),
  bench: f.bench.split("\n").map((s) => s.trim()).filter(Boolean),
  substitutions: f.substitutions.split("\n").map((s) => s.trim()).filter(Boolean),
  formation: f.formation || null,
});

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="font-mono-score text-[11px] uppercase tracking-widest text-slate-400">{label}</Label>
    {children}
  </div>
);

const Editor = ({ initial, onSaved }) => {
  const [form, setForm] = useState(initial ? toForm(initial) : EMPTY);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [squad, setSquad] = useState([]);
  const isCronica = form.category === "cronica";
  const field = "w-full rounded-xl border border-edge bg-ink2 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-crimson focus:outline-none";

  useEffect(() => {
    if (open) API.get("/players").then(({ data }) => setSquad(data)).catch(() => {});
  }, [open]);

  const toggleLineup = (name) => {
    const lines = form.lineup.split("\n").map((s) => s.trim()).filter(Boolean);
    const nextLines = lines.includes(name) ? lines.filter((l) => l !== name) : [...lines, name];
    setForm({ ...form, lineup: nextLines.join("\n") });
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("El título es obligatorio");
    setBusy(true);
    try {
      if (initial) await API.put(`/admin/articles/${initial.id}`, toPayload(form));
      else await API.post("/admin/articles", toPayload(form));
      toast.success(initial ? "Contenido actualizado" : "Contenido publicado");
      setOpen(false);
      setForm(EMPTY);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid={initial ? `admin-edit-${initial.slug}` : "admin-new-article-button"}
          className={`gap-2 rounded-full font-display font-bold uppercase tracking-wider ${initial ? "border border-edge bg-transparent text-slate-300 hover:text-white" : "bg-crimson hover:bg-glow"}`}
        >
          {initial ? <Pencil className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {initial ? "Editar" : "Nuevo contenido"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto border-edge bg-ink2 sm:max-w-2xl" data-testid="admin-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold uppercase text-white">
            {initial ? "Editar contenido" : "Nuevo contenido"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Field label="Título">
              <Input data-testid="editor-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={field} placeholder="Título del artículo o crónica" />
            </Field>
          </div>
          <Field label="Tipo">
            <select data-testid="editor-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={field}>
              <option value="articulo">Artículo</option>
              <option value="cronica">Crónica de partido</option>
            </select>
          </Field>
          <Field label="Etiquetas (separadas por comas)">
            <Input data-testid="editor-tags" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className={field} placeholder="Sporting de Gijón, Opinión" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Entradilla">
              <Textarea data-testid="editor-excerpt" rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className={field} placeholder="Resumen corto que aparecerá en las tarjetas" />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Imagen de portada">
              <div className="flex flex-wrap items-center gap-3">
                <Input data-testid="editor-cover" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} className={field + " flex-1 min-w-[200px]"} placeholder="Pega una URL o sube tu propia imagen" />
                <ImageUpload onUploaded={(url) => setForm((f) => ({ ...f, cover_image: url }))} />
              </div>
              {form.cover_image && (
                <img src={form.cover_image} alt="Portada" className="mt-3 h-28 w-48 rounded-xl border border-edge object-cover" data-testid="editor-cover-preview" />
              )}
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Contenido (usa ## para títulos, > para citas, - para listas)">
              <Textarea data-testid="editor-content" rows={10} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className={field} />
            </Field>
          </div>
          <div className="flex items-center gap-3 sm:col-span-2">
            <Switch data-testid="editor-featured" checked={!!form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} />
            <Label className="text-sm text-white">Destacar en la portada</Label>
          </div>

          {isCronica && (
            <>
              <div className="sm:col-span-2">
                <p className="mb-2 font-display text-lg font-bold uppercase text-crimson">Datos del partido</p>
              </div>
              <Field label="Competición y jornada">
                <Input data-testid="editor-competition" value={form.competition} onChange={(e) => setForm({ ...form, competition: e.target.value })} className={field} placeholder="LaLiga Hypermotion - Jornada 30" />
              </Field>
              <Field label="Fecha del partido">
                <Input data-testid="editor-match-date" value={form.match_date} onChange={(e) => setForm({ ...form, match_date: e.target.value })} className={field} placeholder="2026-03-08" />
              </Field>
              <Field label="Equipo local">
                <Input data-testid="editor-home-team" value={form.home_team} onChange={(e) => setForm({ ...form, home_team: e.target.value })} className={field} />
              </Field>
              <Field label="Equipo visitante">
                <Input data-testid="editor-away-team" value={form.away_team} onChange={(e) => setForm({ ...form, away_team: e.target.value })} className={field} />
              </Field>
              <Field label="Goles local">
                <Input data-testid="editor-home-score" type="number" value={form.home_score} onChange={(e) => setForm({ ...form, home_score: e.target.value })} className={field} />
              </Field>
              <Field label="Goles visitante">
                <Input data-testid="editor-away-score" type="number" value={form.away_score} onChange={(e) => setForm({ ...form, away_score: e.target.value })} className={field} />
              </Field>
              <Field label="Formación (para dibujar el campo)">
                <select data-testid="editor-formation" value={form.formation} onChange={(e) => setForm({ ...form, formation: e.target.value })} className={field}>
                  <option value="">Sin formación</option>
                  {["4-4-2", "4-3-3", "4-2-3-1", "3-5-2", "5-3-2", "3-4-3", "4-1-4-1", "5-4-1"].map((f) => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </Field>
              <div className="sm:col-span-2">
                <Field label="Goles (uno por línea: 72' Gol del Sporting)">
                  <Textarea data-testid="editor-scorers" rows={3} value={form.scorers} onChange={(e) => setForm({ ...form, scorers: e.target.value })} className={field} />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Calificaciones (una por línea: Jugador:8.5)">
                  <Textarea data-testid="editor-ratings" rows={4} value={form.player_ratings} onChange={(e) => setForm({ ...form, player_ratings: e.target.value })} className={field} />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Claves tácticas (una por línea: Título|Texto)">
                  <Textarea data-testid="editor-keys" rows={4} value={form.tactical_keys} onChange={(e) => setForm({ ...form, tactical_keys: e.target.value })} className={field} />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Once inicial del Sporting (un jugador por línea)">
                  <Textarea data-testid="editor-lineup" rows={11} value={form.lineup} onChange={(e) => setForm({ ...form, lineup: e.target.value })} className={field} placeholder={"Rubén Yáñez\nGuille Rosas\n..."} />
                </Field>
              </div>
              {squad.length > 0 && (
                <div className="sm:col-span-2" data-testid="lineup-picker">
                  <p className="mb-2 font-mono-score text-[11px] uppercase tracking-widest text-slate-400">
                    O elige de la plantilla (clic para añadir/quitar del once, en orden)
                  </p>
                  <div className="flex max-h-36 flex-wrap gap-2 overflow-y-auto rounded-xl border border-edge bg-ink p-3">
                    {squad.map((p) => {
                      const selected = form.lineup.split("\n").map((s) => s.trim()).includes(p.name);
                      return (
                        <button
                          key={p.number}
                          type="button"
                          data-testid={`lineup-chip-${p.number}`}
                          onClick={() => toggleLineup(p.name)}
                          className={`rounded-full border px-3 py-1.5 font-mono-score text-xs transition-colors ${
                            selected
                              ? "border-crimson bg-crimson text-white"
                              : "border-edge text-slate-400 hover:border-crimson/60 hover:text-white"
                          }`}
                        >
                          {p.number} · {p.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="sm:col-span-2">
                <Field label="Banquillo (un jugador por línea)">
                  <Textarea data-testid="editor-bench" rows={6} value={form.bench} onChange={(e) => setForm({ ...form, bench: e.target.value })} className={field} placeholder={"Iván Cuéllar\nEric Curbelo\n..."} />
                </Field>
              </div>
              {squad.length > 0 && (
                <div className="sm:col-span-2" data-testid="bench-picker">
                  <p className="mb-2 font-mono-score text-[11px] uppercase tracking-widest text-slate-400">
                    O elige el banquillo de la plantilla (clic para añadir/quitar)
                  </p>
                  <div className="flex max-h-36 flex-wrap gap-2 overflow-y-auto rounded-xl border border-edge bg-ink p-3">
                    {squad.map((p) => {
                      const selected = form.bench.split("\n").map((s) => s.trim()).includes(p.name);
                      return (
                        <button
                          key={p.number}
                          type="button"
                          data-testid={`bench-chip-${p.number}`}
                          onClick={() => {
                            const lines = form.bench.split("\n").map((s) => s.trim()).filter(Boolean);
                            const nextLines = lines.includes(p.name) ? lines.filter((l) => l !== p.name) : [...lines, p.name];
                            setForm({ ...form, bench: nextLines.join("\n") });
                          }}
                          className={`rounded-full border px-3 py-1.5 font-mono-score text-xs transition-colors ${
                            selected
                              ? "border-crimson bg-crimson text-white"
                              : "border-edge text-slate-400 hover:border-crimson/60 hover:text-white"
                          }`}
                        >
                          {p.number} · {p.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="sm:col-span-2">
                <Field label="Sustituciones (una por línea: 65' Entra X por Y)">
                  <Textarea data-testid="editor-substitutions" rows={4} value={form.substitutions} onChange={(e) => setForm({ ...form, substitutions: e.target.value })} className={field} placeholder={"65' Entra Gaspar Campos por Dubasin"} />
                </Field>
              </div>
              {squad.length > 0 && (
                <div className="sm:col-span-2" data-testid="subs-picker">
                  <p className="mb-2 font-mono-score text-[11px] uppercase tracking-widest text-slate-400">
                    O añade un cambio pulsando al jugador que entra (completa minuto y saliente)
                  </p>
                  <div className="flex max-h-36 flex-wrap gap-2 overflow-y-auto rounded-xl border border-edge bg-ink p-3">
                    {squad.map((p) => (
                      <button
                        key={p.number}
                        type="button"
                        data-testid={`subs-chip-${p.number}`}
                        onClick={() =>
                          setForm({
                            ...form,
                            substitutions: (form.substitutions ? form.substitutions + "\n" : "") + `Entra ${p.name} por `,
                          })
                        }
                        className="rounded-full border border-edge px-3 py-1.5 font-mono-score text-xs text-slate-400 transition-colors hover:border-crimson/60 hover:text-white"
                      >
                        {p.number} · {p.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        <Button
          onClick={save}
          disabled={busy}
          data-testid="editor-save-button"
          className="mt-2 w-full rounded-full bg-crimson py-3 font-display text-lg font-bold uppercase tracking-wider hover:bg-glow"
        >
          {busy ? "Guardando..." : initial ? "Guardar cambios" : "Publicar"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

const AdminPanel = () => {
  const { user, loading, logout, formatApiErrorDetail } = useAuth();
  const [items, setItems] = useState([]);
  const [messages, setMessages] = useState([]);
  const [tab, setTab] = useState("articulo");
  const navigate = useNavigate();

  const load = () => {
    API.get("/articles", { params: { limit: 100 } }).then(({ data }) => setItems(data));
    API.get("/admin/messages").then(({ data }) => setMessages(data)).catch(() => {});
  };

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user]);

  if (loading)
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <p className="animate-pulse font-display text-2xl uppercase tracking-widest text-slate-500">Cargando...</p>
      </div>
    );

  if (!user)
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-ink text-center">
        <p className="font-display text-2xl font-bold uppercase text-white">Necesitas iniciar sesión</p>
        <Button data-testid="panel-go-login" onClick={() => navigate("/admin")} className="rounded-full bg-crimson font-display font-bold uppercase">
          Ir al login
        </Button>
      </div>
    );

  if (user.role !== "admin")
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-ink text-center">
        <p className="font-display text-2xl font-bold uppercase text-white">Sin permisos</p>
        <p className="text-slate-400">Esta zona es solo para el administrador.</p>
      </div>
    );

  const remove = async (item) => {
    if (!window.confirm(`¿Seguro que quieres borrar "${item.title}"?`)) return;
    try {
      await API.delete(`/admin/articles/${item.id}`);
      toast.success("Contenido eliminado");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const filtered = items.filter((a) => a.category === tab);

  return (
    <div className="min-h-screen bg-ink" data-testid="admin-panel">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-ink/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 md:px-12">
          <div className="flex items-center gap-3">
            <LogoMark className="h-8 w-8" />
            <p className="font-display text-xl font-extrabold uppercase text-white">
              Panel <span className="text-crimson">ENOLFC</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" data-testid="panel-view-site" className="flex items-center gap-2 rounded-full border border-edge px-4 py-2 font-display text-sm font-bold uppercase tracking-wider text-slate-300 hover:text-white">
              <ArrowLeft className="h-4 w-4" /> Ver web
            </a>
            <Button
              data-testid="panel-logout"
              onClick={async () => { await logout(); navigate("/admin"); }}
              className="rounded-full border border-edge bg-transparent text-slate-300 hover:text-white"
            >
              <LogOut className="h-4 w-4" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 md:px-12">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex gap-2" data-testid="panel-tabs">
            {[
              { id: "articulo", label: "Artículos", icon: Newspaper },
              { id: "cronica", label: "Crónicas", icon: Trophy },
              { id: "fans", label: "Afición", icon: MessageSquare },
              { id: "tiktok", label: "TikTok", icon: Music2 },
              { id: "partido", label: "Partido", icon: CalendarClock },
              { id: "plantilla", label: "Plantilla", icon: Users },
              { id: "newsletter", label: "Newsletter", icon: MailCheck },
              { id: "messages", label: "Mensajes", icon: Inbox },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                data-testid={`panel-tab-${id}`}
                onClick={() => setTab(id)}
                className={`flex items-center gap-2 rounded-full px-5 py-2.5 font-display text-base font-bold uppercase tracking-wider transition-colors ${
                  tab === id ? "bg-crimson text-white" : "border border-edge text-slate-400 hover:text-white"
                }`}
              >
                <Icon className="h-4 w-4" /> {label}
              </button>
            ))}
          </div>
          {(tab === "articulo" || tab === "cronica") && <Editor onSaved={load} />}
        </div>

        {tab === "fans" ? (
          <FansModeration />
        ) : tab === "tiktok" ? (
          <TikTokManager />
        ) : tab === "partido" ? (
          <MatchEditor />
        ) : tab === "plantilla" ? (
          <SquadManager />
        ) : tab === "newsletter" ? (
          <SubscribersList />
        ) : tab === "messages" ? (
          <div className="mt-8 space-y-4" data-testid="panel-messages">
            {messages.length === 0 ? (
              <p className="py-16 text-center text-slate-500">No hay mensajes todavía.</p>
            ) : (
              messages.map((m) => (
                <div key={m._id || m.id} className="rounded-2xl border border-edge bg-ink2 p-6" data-testid="panel-message-card">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-display text-lg font-bold uppercase text-white">{m.subject || "Sin asunto"}</p>
                    <p className="font-mono-score text-xs text-slate-500">{m.created_at?.slice(0, 10)}</p>
                  </div>
                  <p className="mt-1 text-sm text-crimson">{m.name} · {m.email}</p>
                  <p className="mt-3 text-sm leading-relaxed text-slate-300">{m.message}</p>
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="mt-8 space-y-4" data-testid={`panel-list-${tab}`}>
            {filtered.length === 0 ? (
              <p className="py-16 text-center text-slate-500">
                No hay {tab === "cronica" ? "crónicas" : "artículos"} todavía. ¡Crea el primero!
              </p>
            ) : (
              filtered.map((item) => (
                <div key={item.id} className="flex flex-wrap items-center gap-4 rounded-2xl border border-edge bg-ink2 p-4" data-testid={`panel-item-${item.slug}`}>
                  <img src={item.cover_image} alt="" className="h-16 w-24 rounded-lg object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-display text-lg font-bold uppercase text-white">{item.title}</p>
                    <p className="font-mono-score text-xs text-slate-500">
                      {item.created_at?.slice(0, 10)} {item.featured && <span className="ml-2 text-glow">★ Destacado</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Editor initial={item} onSaved={load} />
                    <Button
                      data-testid={`admin-delete-${item.slug}`}
                      onClick={() => remove(item)}
                      className="rounded-full border border-edge bg-transparent text-slate-400 hover:border-crimson hover:text-crimson"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPanel;
